// src/context/DataProvider.jsx
import React, { createContext, useContext, useEffect, useMemo, useRef, useState } from 'react'
import { DateTime } from 'luxon'
import { getTrips } from '../api/user'
import { getBillingInfo } from '../api/billing'
import { getCards } from '../api/cards'
import { AuthContext } from '../AuthProvider'

const DataContext = createContext(null)

export function DataProvider({ children }) {
  const Auth = useContext(AuthContext)
  const [cards, setCards] = useState([])
  const [pickCard, setPickCard] = useState(null)
  const [trips, setTrips] = useState([])
  const [billing, setBilling] = useState(null)
  const [loading, setLoading] = useState(false)
  const firstLoadDone = useRef(false)
  const poller = useRef(null)

  const computePickCardBalance = (pickCard, trips) => {
    if (!pickCard) return null

    const transfers = Array.isArray(pickCard.transfers) ? pickCard.transfers : []
    const transferSum = transfers.reduce((sum, t) => {
      const amt = Number(t?.amount) || 0
      const typ = Number(t?.type)
      const signed = typ === 2 ? -amt : amt
      return sum + signed
    }, 0)

    const pcPan = pickCard.cardNumber || pickCard.number || pickCard.PAN || ''
    const tripsSum = (Array.isArray(trips) ? trips : []).reduce((sum, trip) => {
      const tripPan = trip.cardPAN || trip.cardNumber || trip.PAN || ''
      const sameCard =
        tripPan === pcPan ||
        (pcPan && tripPan && pcPan.slice(-4) === tripPan.slice(-4))

      const amt = Number(trip?.amount) || 0
      return sameCard ? sum + amt : sum
    }, 0)

    const balance = transferSum - tripsSum
    return { ...pickCard, balance: Number(balance.toFixed(2)) }
  }

  const fetchAll = async (label = 'initial') => {
    try {
      if (!firstLoadDone.current) setLoading(true)
      console.log(`[data] fetch ${label} @ ${DateTime.local().toISOTime()}`)
      const [fetchedTrips, fetchedBilling, fetchedCards] = await Promise.all([
        getTrips(),
        getBillingInfo(),
        getCards()
      ])
      setTrips(fetchedTrips || [])
      setBilling(fetchedBilling || null)
      setCards(fetchedCards || [])
      const pc = (fetchedCards || []).find(c => c.pick) || null
      const pickWithBalance = computePickCardBalance(pc, fetchedTrips)
      setPickCard(pickWithBalance)
    } catch (e) {
      console.error('[data] fetch error', e)
    } finally {
      if (!firstLoadDone.current) {
        firstLoadDone.current = true
        setLoading(false)
      }
    }
  }

  useEffect(() => {
    if (!Auth) return
    fetchAll('initial')
    poller.current = setInterval(() => fetchAll('poll'), 3000)
    return () => {
      if (poller.current) clearInterval(poller.current)
    }
  }, [Auth])

  const value = useMemo(() => ({
    loading,
    cards,
    pickCard,
    trips,
    billing,
    refetch: () => fetchAll('manual')
  }), [loading, cards, pickCard, trips, billing])

  return <DataContext.Provider value={value}>{children}</DataContext.Provider>
}

export const useData = () => {
  const ctx = useContext(DataContext)
  if (!ctx) throw new Error('useData must be used within DataProvider')
  return ctx
}

export const useCards = () => {
  const { cards, pickCard, loading, refetch } = useData()
  return { cards, pickCard, loading, refetch }
}

export const useTrips = () => {
  const { trips, loading, refetch } = useData()
  return { trips, loading, refetch }
}

export const useBilling = () => {
  const { billing, loading, refetch } = useData()
  return { billing, loading, refetch }
}

export const useTripsFiltered = ({ home = false } = {}) => {
  const { trips, pickCard, loading, refetch } = useData()

  const filtered = useMemo(() => {
    const last4 = s => String(s ?? '').slice(-4)
    const pickLast4 = last4(pickCard?.number || pickCard?.cardNumber || pickCard?.PAN)

    if (!Array.isArray(trips)) return []

    if (!pickCard) {
      return home ? trips.slice(0, 3) : trips
    }

    if (pickCard.status === 'active') {
      const next = trips.filter(trip => {
        const external = trip.merchant !== 'UbiRider'
        return external ? last4(trip.cardPAN) === pickLast4 : true
      })
      return home ? next.slice(0, 3) : next
    }

    const next = trips.filter(trip => {
      const external = trip.merchant !== 'UbiRider'
      return last4(trip.cardPAN) !== pickLast4 && !external
    })

    return home ? next.slice(0, 3) : next
  }, [trips, pickCard, home])

  return { trips: filtered, loading, refetch }
}